// src/pages/inventory/InventoryManagement.js
// Página de Inventario y Reportería - Panel de administración

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Spinner, Alert, Modal, Tabs, Tab, Badge } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import { useAuth } from '../../context/AuthContext';
import {
  getInventoryItems,
  getInventoryItemById,
  getMovementsReport,
  getStockSummaryReport,
  getLowStockReport,
  getValuationReport,
  getInOutSummaryReport,
  postInventoryAdjust
} from '../../services/inventoryApi';
import { getProducts } from '../../services/productApi';
import { dotoniwoofApi } from '../../api/dotoniwooApi';
import { downloadCSV } from '../../utils/csvTemplateGenerator';
import './InventoryManagement.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const formatterCOP = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const formatNumber = (n) => {
  if (n == null || isNaN(n)) return '-';
  return new Intl.NumberFormat('es-CO').format(n);
};

/** Resuelve el peso de un ítem desde cualquier atributo posible */
const getWeightDisplay = (it, weights = []) => {
  // Backend puede devolver: weight (string), weight_name, weight.weight_name, gramaje
  const name = it.weight_name || (typeof it.weight === 'string' ? it.weight : it.weight?.weight_name) || it.gramaje;
  if (name && String(name).toUpperCase() !== 'N/A') return name;
  const id = it.id_weight ?? it.weight?.id_weight;
  if (id != null && weights.length) {
    const w = weights.find((w) => String(w.id_weight ?? w.id) === String(id));
    return w?.weight_name || w?.name || '-';
  }
  return '-';
};

/** Resuelve color desde color, color_name o color.color_name */
const getColorDisplay = (it) => it.color ?? it.color_name ?? it.color?.color_name ?? '-';

/** Resuelve talla desde size, size_name o size.size_name */
const getSizeDisplay = (it) => it.size ?? it.size_name ?? it.size?.size_name ?? '-';

/** Escapa un valor para CSV */
const escapeCSV = (val) => {
  const s = String(val ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const monthAgoStr = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// --- Modal Detalle de Item ---
const ItemDetailModal = ({ show, onHide, idItem, onRefresh, weights = [] }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !idItem) return;
    setLoading(true);
    setError('');
    getInventoryItemById(idItem).then((res) => {
      if (res.ok) setDetail(res.data);
      else setError(res.data?.message || 'No se pudo cargar el detalle.');
      setLoading(false);
    });
  }, [show, idItem]);

  const movements = detail?.last_movements ?? [];

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="inventory-modal">
      <Modal.Header closeButton>
        <Modal.Title>Detalle del ítem</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="inventory-loading">
            <Spinner animation="border" variant="primary" />
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {detail && !loading && (
          <>
            <div className="inventory-detail-info">
              <Row>
                <Col md={6}>
                  <div className="info-item"><span className="label">SKU</span><span>{detail.sku || '-'}</span></div>
                  <div className="info-item"><span className="label">Descripción</span><span>{detail.description || '-'}</span></div>
                  <div className="info-item"><span className="label">Producto</span><span>{detail.product_name || '-'}</span></div>
                </Col>
                <Col md={6}>
                  <div className="info-item"><span className="label">Color</span><span>{getColorDisplay(detail)}</span></div>
                  <div className="info-item"><span className="label">Talla</span><span>{getSizeDisplay(detail)}</span></div>
                  <div className="info-item"><span className="label">Peso</span><span>{getWeightDisplay(detail, weights)}</span></div>
                  <div className="info-item"><span className="label">Stock</span><strong>{formatNumber(detail.stock)}</strong></div>
                  <div className="info-item"><span className="label">Costo prom.</span><span>{formatterCOP.format(detail.cost_avg ?? 0)}</span></div>
                  <div className="info-item"><span className="label">Costo último</span><span>{formatterCOP.format(detail.cost_last ?? 0)}</span></div>
                </Col>
              </Row>
            </div>
            <h6 className="mt-3 mb-2" style={{ color: '#332E5A', fontWeight: 600 }}>Últimos movimientos</h6>
            <div className="inventory-table-wrap">
              <Table size="sm" striped bordered hover className="inventory-detail-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Entrada</th>
                    <th>Salida</th>
                    <th>Costo unit.</th>
                    <th>Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.length === 0 && <tr><td colSpan={6} className="text-center">Sin movimientos</td></tr>}
                  {movements.map((m) => (
                    <tr key={m.id_inventory_move ?? m.move_date + m.quantity_in + m.quantity_out}>
                      <td>{m.move_date || '-'}</td>
                      <td>{m.doc_type || '-'}</td>
                      <td>{formatNumber(m.quantity_in)}</td>
                      <td>{formatNumber(m.quantity_out)}</td>
                      <td>{formatterCOP.format(m.unit_cost ?? 0)}</td>
                      <td>{m.ref_table && m.ref_id ? `${m.ref_table}#${m.ref_id}` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

// --- Modal Ajuste Manual ---
const AdjustModal = ({ show, onHide, onSuccess, items }) => {
  const { user } = useAuth();
  const [idItem, setIdItem] = useState('');
  const [quantity, setQuantity] = useState('');
  const [adjustmentType, setAdjustmentType] = useState('INCREMENT');
  const [unitCost, setUnitCost] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setIdItem('');
    setQuantity('');
    setAdjustmentType('INCREMENT');
    setUnitCost('');
    setNotes('');
    setError('');
  };

  const handleClose = () => {
    reset();
    onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!user?.uid) {
      setError('Sesión no válida.');
      return;
    }
    const qty = parseInt(quantity, 10);
    if (!idItem || !quantity || isNaN(qty) || qty < 1) {
      setError('Selecciona un ítem y una cantidad válida.');
      return;
    }
    setLoading(true);
    const payload = {
      firebase_UID: user.uid,
      id_item: parseInt(idItem, 10),
      quantity: qty,
      adjustment_type: adjustmentType
    };
    if (unitCost && !isNaN(parseFloat(unitCost))) payload.unit_cost = parseFloat(unitCost);
    if (notes?.trim()) payload.notes = notes.trim();

    const res = await postInventoryAdjust(payload);
    setLoading(false);
    if (res.ok && (res.status === 200 || res.status === 201 || res.code === 200 || res.code === 201)) {
      if (onSuccess) onSuccess();
      handleClose();
    } else {
      setError(res.data?.message || 'Error al realizar el ajuste.');
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="inventory-modal">
      <Modal.Header closeButton>
        <Modal.Title>Ajuste manual</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Ítem <span className="text-danger">*</span></Form.Label>
            <Form.Select value={idItem} onChange={(e) => setIdItem(e.target.value)} required>
              <option value="">Selecciona ítem</option>
              {(items || []).map((it) => (
                <option key={it.id_item} value={it.id_item}>
                  {it.sku || it.id_item} - {it.description || it.product_name || '-'}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Cantidad <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Tipo de ajuste</Form.Label>
            <Form.Select value={adjustmentType} onChange={(e) => setAdjustmentType(e.target.value)}>
              <option value="INCREMENT">Incrementar</option>
              <option value="DECREMENT">Decrementar</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Costo unitario (opcional)</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              placeholder="0"
              value={unitCost}
              onChange={(e) => setUnitCost(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Notas (opcional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Form.Group>
          <div className="d-flex gap-2 justify-content-end">
            <Button variant="outline-secondary" onClick={handleClose}>Cancelar</Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Enviando…' : 'Enviar'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

// --- Modal Reportes ---
const ReportModal = ({ show, onHide, reportType, params }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !reportType) return;
    setLoading(true);
    setError('');
    setData(null);

    const load = async () => {
      try {
        if (reportType === 'stock-summary') {
          const res = await getStockSummaryReport(params || {});
          if (res.ok) setData(res.data);
          else setError(res.data?.message || 'Error');
        } else if (reportType === 'low-stock') {
          const res = await getLowStockReport(params?.threshold ?? 5);
          if (res.ok) setData({ items: res.data, threshold: res.threshold });
          else setError(res.data?.message || 'Error');
        } else if (reportType === 'valuation') {
          const res = await getValuationReport();
          if (res.ok) setData(res.data);
          else setError(res.data?.message || 'Error');
        } else if (reportType === 'in-out-summary') {
          const res = await getInOutSummaryReport(params?.date_from, params?.date_to);
          if (res.ok) setData(res.data);
          else setError(res.data?.message || 'Error');
        }
      } catch (e) {
        setError('Error al cargar el reporte.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [show, reportType, params?.threshold, params?.date_from, params?.date_to]);

  const chartData = reportType === 'valuation' && data?.by_category?.length
    ? {
        labels: data.by_category.map((c) => c.category_name || c.name || 'Sin categoría'),
        datasets: [{
          data: data.by_category.map((c) => parseFloat(c.valuation ?? c.total ?? 0) || 0),
          backgroundColor: ['#EA9D0E', '#ffde59', '#332E5A', '#31a184', '#DF5931', '#4a4470'],
          borderWidth: 0
        }]
      }
    : null;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="inventory-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {reportType === 'stock-summary' && 'Stock por categoría'}
          {reportType === 'low-stock' && 'Stock bajo'}
          {reportType === 'valuation' && 'Valoración del inventario'}
          {reportType === 'in-out-summary' && 'Entradas y salidas por período'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="inventory-loading">
            <Spinner animation="border" variant="primary" />
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {!loading && !error && reportType === 'valuation' && data && (
          <>
            <div className="report-valuation-total mb-4">
              Valor total: <strong>{formatterCOP.format(data.total_valuation ?? 0)}</strong>
            </div>
            {chartData && chartData.labels.length > 0 && (
              <div className="report-chart">
                <Doughnut data={chartData} options={{ responsive: true, maintainAspectRatio: true }} />
              </div>
            )}
            {data.by_category?.length > 0 && (
              <Table size="sm" striped bordered className="mt-3">
                <thead><tr><th>Categoría</th><th>Valoración</th></tr></thead>
                <tbody>
                  {data.by_category.map((c, i) => (
                    <tr key={i}>
                      <td>{c.category_name || c.name || '-'}</td>
                      <td>{formatterCOP.format(c.valuation ?? c.total ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
        {!loading && !error && reportType === 'low-stock' && data?.items && (
          <div className="inventory-table-wrap">
            <p>Items con stock ≤ {data.threshold ?? 5}</p>
            <Table size="sm" striped bordered hover>
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Descripción</th>
                  <th>Stock</th>
                  <th>Costo prom.</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && <tr><td colSpan={4} className="text-center">Ninguno</td></tr>}
                {data.items.map((it) => (
                  <tr key={it.id_item}>
                    <td>{it.sku || '-'}</td>
                    <td>{it.description || it.product_name || '-'}</td>
                    <td><Badge bg="danger">{formatNumber(it.stock)}</Badge></td>
                    <td>{formatterCOP.format(it.cost_avg ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {!loading && !error && reportType === 'in-out-summary' && Array.isArray(data) && (
          <Table size="sm" striped bordered hover>
            <thead>
              <tr><th>Tipo doc.</th><th>Total entradas</th><th>Total salidas</th></tr>
            </thead>
            <tbody>
              {data.length === 0 && <tr><td colSpan={3} className="text-center">Sin datos</td></tr>}
              {data.map((r, i) => (
                <tr key={i}>
                  <td>{r.doc_type || '-'}</td>
                  <td>{formatNumber(r.total_in)}</td>
                  <td>{formatNumber(r.total_out)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {!loading && !error && reportType === 'stock-summary' && data && (
          <div className="inventory-table-wrap">
            <p className="mb-2">Valoración total: {formatterCOP.format(data.total_valuation ?? 0)}</p>
            {data.items?.length > 0 && (
              <Table size="sm" striped bordered hover>
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Descripción</th>
                    <th>Stock</th>
                    <th>Costo prom.</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it) => (
                    <tr key={it.id_item}>
                      <td>{it.sku || '-'}</td>
                      <td>{it.description || it.product_name || '-'}</td>
                      <td>{formatNumber(it.stock)}</td>
                      <td>{formatterCOP.format(it.cost_avg ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

// --- Componente principal ---
const InventoryManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('stock');

  // KPIs
  const [kpiTotalValuation, setKpiTotalValuation] = useState(0);
  const [kpiLowStockCount, setKpiLowStockCount] = useState(0);
  const [kpiTotalItems, setKpiTotalItems] = useState(0);
  const [kpiLastMovements, setKpiLastMovements] = useState([]);
  const [kpiLoading, setKpiLoading] = useState(true);

  // Stock
  const [stockItems, setStockItems] = useState([]);
  const [stockPage, setStockPage] = useState(1);
  const [stockTotalPages, setStockTotalPages] = useState(1);
  const [stockTotalRows, setStockTotalRows] = useState(0);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockDownloading, setStockDownloading] = useState(false);
  const [stockFilters, setStockFilters] = useState({ id_product: '', sku: '', stock_min: '', stock_max: '', id_weight: '', id_color: '', id_size: '' });

  // Movements
  const [movements, setMovements] = useState([]);
  const [movPage, setMovPage] = useState(1);
  const [movTotalPages, setMovTotalPages] = useState(1);
  const [movTotalRows, setMovTotalRows] = useState(0);
  const [movLoading, setMovLoading] = useState(false);
  const [movFilters, setMovFilters] = useState({ id_item: '', date_from: monthAgoStr(), date_to: todayStr(), movement_type: '', doc_type: '' });

  // Modals
  const [showItemDetail, setShowItemDetail] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [showAdjust, setShowAdjust] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportType, setReportType] = useState('');
  const [reportParams, setReportParams] = useState({});
  const [reportDateFrom, setReportDateFrom] = useState(monthAgoStr());
  const [reportDateTo, setReportDateTo] = useState(todayStr());
  const [selectedReportType, setSelectedReportType] = useState('');

  const [products, setProducts] = useState([]);
  const [weights, setWeights] = useState([]);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [allItemsForAdjust, setAllItemsForAdjust] = useState([]);
  const [alertMsg, setAlertMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    getProducts().then((r) => {
      if (r.ok && r.data) {
        const list = r.data?.message ?? r.data?.data ?? (Array.isArray(r.data) ? r.data : []);
        setProducts(Array.isArray(list) ? list : []);
      }
    });
  }, []);

  useEffect(() => {
    dotoniwoofApi.get('/weight.php').then((res) => {
      const data = res.data;
      const list = data?.message ?? data?.data ?? (Array.isArray(data) ? data : []);
      setWeights(Array.isArray(list) ? list : []);
    }).catch(() => setWeights([]));
  }, []);

  useEffect(() => {
    dotoniwoofApi.get('/color.php').then((res) => {
      const data = res.data;
      const list = data?.message ?? data?.data ?? (Array.isArray(data) ? data : []);
      setColors(Array.isArray(list) ? list : []);
    }).catch(() => setColors([]));
  }, []);

  useEffect(() => {
    dotoniwoofApi.get('/size.php').then((res) => {
      const data = res.data;
      const list = data?.message ?? data?.data ?? (Array.isArray(data) ? data : []);
      setSizes(Array.isArray(list) ? list : []);
    }).catch(() => setSizes([]));
  }, []);

  const loadKpis = useCallback(async () => {
    setKpiLoading(true);
    try {
      const [valRes, lowRes, itemsRes, movRes] = await Promise.all([
        getValuationReport(),
        getLowStockReport(5),
        getInventoryItems({ page: 1, limit: 1 }),
        getMovementsReport({ page: 1, limit: 5 })
      ]);
      if (valRes.ok && valRes.data) setKpiTotalValuation(valRes.data.total_valuation ?? 0);
      if (lowRes.ok && Array.isArray(lowRes.data)) setKpiLowStockCount(lowRes.data.length);
      if (itemsRes.ok) setKpiTotalItems(itemsRes.total_rows ?? 0);
      if (movRes.ok) setKpiLastMovements(movRes.data ?? []);
    } catch (e) {
      console.error('Error cargando KPIs:', e);
    } finally {
      setKpiLoading(false);
    }
  }, []);

  useEffect(() => { loadKpis(); }, [loadKpis]);

  const loadStock = useCallback(async (pageOverride) => {
    setStockLoading(true);
    const page = pageOverride ?? stockPage;
    const params = { page, limit: 20 };
    if (stockFilters.id_product) params.id_product = stockFilters.id_product;
    if (stockFilters.sku) params.sku = stockFilters.sku;
    if (stockFilters.stock_min !== '') params.stock_min = stockFilters.stock_min;
    if (stockFilters.stock_max !== '') params.stock_max = stockFilters.stock_max;
    if (stockFilters.id_weight) params.id_weight = stockFilters.id_weight;
    const res = await getInventoryItems(params);
    if (res.ok) {
      setStockItems(res.data ?? []);
      setStockTotalPages(res.total_pages ?? 1);
      setStockTotalRows(res.total_rows ?? 0);
    } else {
      setStockItems([]);
    }
    setStockLoading(false);
  }, [stockPage, stockFilters]);

  useEffect(() => { loadStock(); }, [loadStock]);

  const loadMovements = useCallback(async (pageOverride) => {
    setMovLoading(true);
    const page = pageOverride ?? movPage;
    const params = { page, limit: 20 };
    if (movFilters.id_item) params.id_item = movFilters.id_item;
    if (movFilters.date_from) params.date_from = movFilters.date_from;
    if (movFilters.date_to) params.date_to = movFilters.date_to;
    if (movFilters.movement_type) params.movement_type = movFilters.movement_type;
    if (movFilters.doc_type) params.doc_type = movFilters.doc_type;
    const res = await getMovementsReport(params);
    if (res.ok) {
      setMovements(res.data ?? []);
      setMovTotalPages(res.total_pages ?? 1);
      setMovTotalRows(res.total_rows ?? 0);
    } else {
      setMovements([]);
    }
    setMovLoading(false);
  }, [movPage, movFilters]);

  useEffect(() => { loadMovements(); }, [loadMovements]);

  const filteredMovements = useMemo(() => {
    let result = movements;
    if (movFilters.movement_type === 'in') result = result.filter((m) => (m.quantity_in ?? 0) > 0);
    else if (movFilters.movement_type === 'out') result = result.filter((m) => (m.quantity_out ?? 0) > 0);
    if (movFilters.doc_type) result = result.filter((m) => (m.doc_type || '').toUpperCase() === movFilters.doc_type.toUpperCase());
    return result;
  }, [movements, movFilters.movement_type, movFilters.doc_type]);

  /** Filtros en cliente (peso, color, talla - el backend puede no soportarlos) */
  const filteredStockItems = useMemo(() => {
    let result = stockItems;
    if (stockFilters.id_weight) {
      const selectedWeight = weights.find((w) => String(w.id_weight ?? w.id) === String(stockFilters.id_weight));
      const targetName = (selectedWeight?.weight_name || selectedWeight?.name || '').toUpperCase();
      if (targetName) {
        result = result.filter((it) => {
          const itemWeight = getWeightDisplay(it, weights);
          return itemWeight && String(itemWeight).toUpperCase() === targetName;
        });
      }
    }
    if (stockFilters.id_color) {
      const selectedColor = colors.find((c) => String(c.id_color ?? c.id) === String(stockFilters.id_color));
      const targetName = (selectedColor?.color_name || selectedColor?.name || '').toUpperCase();
      if (targetName) {
        result = result.filter((it) => {
          const itemColor = getColorDisplay(it);
          return itemColor && String(itemColor).toUpperCase() === targetName;
        });
      }
    }
    if (stockFilters.id_size) {
      const selectedSize = sizes.find((s) => String(s.id_size ?? s.id) === String(stockFilters.id_size));
      const targetName = (selectedSize?.size_name || selectedSize?.name || '').toUpperCase();
      if (targetName) {
        result = result.filter((it) => {
          const itemSize = getSizeDisplay(it);
          return itemSize && String(itemSize).toUpperCase() === targetName;
        });
      }
    }
    return result;
  }, [stockItems, stockFilters.id_weight, stockFilters.id_color, stockFilters.id_size, weights, colors, sizes]);

  const loadItemsForAdjust = useCallback(async () => {
    const res = await getInventoryItems({ page: 1, limit: 200 });
    if (res.ok && res.data) setAllItemsForAdjust(res.data);
  }, []);

  useEffect(() => {
    if (showAdjust) loadItemsForAdjust();
  }, [showAdjust, loadItemsForAdjust]);

  const handleRowClick = (idItem) => {
    setSelectedItemId(idItem);
    setShowItemDetail(true);
  };

  const handleAdjustSuccess = () => {
    loadKpis();
    loadStock();
    loadMovements();
    setAlertMsg({ type: 'success', text: 'Ajuste realizado correctamente.' });
    setTimeout(() => setAlertMsg({}), 3000);
  };

  const handleDownloadStockCSV = useCallback(async () => {
    setStockDownloading(true);
    try {
      const params = { page: 1, limit: 5000 };
      if (stockFilters.id_product) params.id_product = stockFilters.id_product;
      if (stockFilters.sku) params.sku = stockFilters.sku;
      if (stockFilters.stock_min !== '') params.stock_min = stockFilters.stock_min;
      if (stockFilters.stock_max !== '') params.stock_max = stockFilters.stock_max;
      if (stockFilters.id_weight) params.id_weight = stockFilters.id_weight;
      const res = await getInventoryItems(params);
      let items = res.ok ? (res.data ?? []) : [];
      if (stockFilters.id_color) {
        const selectedColor = colors.find((c) => String(c.id_color ?? c.id) === String(stockFilters.id_color));
        const targetName = (selectedColor?.color_name || selectedColor?.name || '').toUpperCase();
        if (targetName) items = items.filter((it) => getColorDisplay(it) && String(getColorDisplay(it)).toUpperCase() === targetName);
      }
      if (stockFilters.id_size) {
        const selectedSize = sizes.find((s) => String(s.id_size ?? s.id) === String(stockFilters.id_size));
        const targetName = (selectedSize?.size_name || selectedSize?.name || '').toUpperCase();
        if (targetName) items = items.filter((it) => getSizeDisplay(it) && String(getSizeDisplay(it)).toUpperCase() === targetName);
      }
      if (stockFilters.id_weight) {
        const selectedWeight = weights.find((w) => String(w.id_weight ?? w.id) === String(stockFilters.id_weight));
        const targetName = (selectedWeight?.weight_name || selectedWeight?.name || '').toUpperCase();
        if (targetName) items = items.filter((it) => { const w = getWeightDisplay(it, weights); return w && String(w).toUpperCase() === targetName; });
      }
      const headers = ['SKU', 'Descripción', 'Producto', 'Color', 'Talla', 'Peso', 'Stock', 'Costo prom.', 'Costo último'];
      const rows = items.map((it) => [
        it.sku ?? '',
        it.description ?? '',
        it.product_name ?? '',
        getColorDisplay(it),
        getSizeDisplay(it),
        getWeightDisplay(it, weights),
        it.stock ?? 0,
        it.cost_avg ?? 0,
        it.cost_last ?? 0
      ]);
      const csvLines = [headers.map(escapeCSV).join(','), ...rows.map((r) => r.map(escapeCSV).join(','))];
      const csvContent = '\uFEFF' + csvLines.join('\n');
      const filename = `stock_actual_${new Date().toISOString().slice(0, 10)}.csv`;
      downloadCSV(csvContent, filename);
    } catch (e) {
      console.error('Error descargando CSV:', e);
      setAlertMsg({ type: 'danger', text: 'Error al descargar el CSV.' });
      setTimeout(() => setAlertMsg({}), 3000);
    } finally {
      setStockDownloading(false);
    }
  }, [stockFilters, weights, colors, sizes]);

  const openReport = (type, params = {}) => {
    setSelectedReportType(type);
    setReportType(type);
    setReportParams(params);
    setShowReport(true);
  };

  return (
    <>
      <NavContainer />
      <OffCanvasNav />
      <Helmet><title>Inventario - DotoniWoof</title></Helmet>
      <Container fluid className="inventory-page">
        <motion.div className="inventory-header" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <h1>
            <span className="header-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
            </span>
            Inventario
          </h1>
          <Button variant="primary" className="btn-adjust" onClick={() => setShowAdjust(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
            Ajuste manual
          </Button>
        </motion.div>

        {alertMsg.text && (
          <Alert variant={alertMsg.type} dismissible onClose={() => setAlertMsg({})} className="mb-3">
            {alertMsg.text}
          </Alert>
        )}

        {/* KPIs */}
        <div className="kpi-row">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.05 }}>
            <Card className="kpi-card">
                <Card.Body>
                  <div className="kpi-icon-wrap">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
                  </div>
                  <div className="kpi-label">Valor total inventario</div>
                  {kpiLoading ? <Spinner size="sm" /> : <div className="kpi-value">{formatterCOP.format(kpiTotalValuation)}</div>}
                </Card.Body>
            </Card>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.1 }}>
              <Card className="kpi-card kpi-warning">
                <Card.Body>
                  <div className="kpi-icon-wrap">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                  </div>
                  <div className="kpi-label">Items con stock bajo</div>
                  {kpiLoading ? <Spinner size="sm" /> : <div className="kpi-value">{kpiLowStockCount}</div>}
                </Card.Body>
            </Card>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.15 }}>
              <Card className="kpi-card">
                <Card.Body>
                  <div className="kpi-icon-wrap">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="7.5 4.21 12 6.81 16.5 4.21" /></svg>
                  </div>
                  <div className="kpi-label">Total items</div>
                  {kpiLoading ? <Spinner size="sm" /> : <div className="kpi-value">{formatNumber(kpiTotalItems)}</div>}
                </Card.Body>
            </Card>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.2 }}>
              <Card className="kpi-card">
                <Card.Body>
                  <div className="kpi-icon-wrap">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
                  </div>
                  <div className="kpi-label">Últimos movimientos</div>
                  {kpiLoading ? <Spinner size="sm" /> : <div className="kpi-value">{kpiLastMovements.length} registros</div>}
                </Card.Body>
            </Card>
          </motion.div>
        </div>

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="inventory-tabs">
          <Tab eventKey="stock" title="Stock actual">
            <Card>
              <Card.Header className="stock-card-header">
                <div className="stock-filters-row">
                  <div className="filter-field-wrap">
                    <span className="filter-field-label">SKU</span>
                    <Form.Control
                      placeholder="Buscar por SKU"
                      value={stockFilters.sku}
                      onChange={(e) => { setStockFilters((f) => ({ ...f, sku: e.target.value })); setStockPage(1); }}
                      className="filter-input"
                    />
                  </div>
                  <div className="filter-field-wrap">
                    <span className="filter-field-label">Producto</span>
                    <Form.Select
                      value={stockFilters.id_product}
                      onChange={(e) => { setStockFilters((f) => ({ ...f, id_product: e.target.value })); setStockPage(1); }}
                      className="filter-select"
                    >
                      <option value="">Todos</option>
                      {products.map((p) => (
                        <option key={p.id_product ?? p.id} value={p.id_product ?? p.id}>{p.name || p.product_name || p.id_product || p.id}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="filter-field-wrap">
                    <span className="filter-field-label">Stock mín</span>
                    <Form.Control
                      type="number"
                      placeholder="—"
                      value={stockFilters.stock_min}
                      onChange={(e) => { setStockFilters((f) => ({ ...f, stock_min: e.target.value })); setStockPage(1); }}
                      className="filter-input filter-input-narrow"
                    />
                  </div>
                  <div className="filter-field-wrap">
                    <span className="filter-field-label">Stock máx</span>
                    <Form.Control
                      type="number"
                      placeholder="—"
                      value={stockFilters.stock_max}
                      onChange={(e) => { setStockFilters((f) => ({ ...f, stock_max: e.target.value })); setStockPage(1); }}
                      className="filter-input filter-input-narrow"
                    />
                  </div>
                  <div className="filter-field-wrap">
                    <span className="filter-field-label">Color</span>
                    <Form.Select
                      value={stockFilters.id_color}
                      onChange={(e) => { setStockFilters((f) => ({ ...f, id_color: e.target.value })); setStockPage(1); }}
                      className="filter-select"
                    >
                      <option value="">Todos</option>
                      {colors.map((c) => (
                        <option key={c.id_color ?? c.id} value={c.id_color ?? c.id}>{c.color_name || c.name || '-'}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="filter-field-wrap">
                    <span className="filter-field-label">Talla</span>
                    <Form.Select
                      value={stockFilters.id_size}
                      onChange={(e) => { setStockFilters((f) => ({ ...f, id_size: e.target.value })); setStockPage(1); }}
                      className="filter-select"
                    >
                      <option value="">Todos</option>
                      {sizes.map((s) => (
                        <option key={s.id_size ?? s.id} value={s.id_size ?? s.id}>{s.size_name || s.name || '-'}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="filter-field-wrap">
                    <span className="filter-field-label">Peso</span>
                    <Form.Select
                      value={stockFilters.id_weight}
                      onChange={(e) => { setStockFilters((f) => ({ ...f, id_weight: e.target.value })); setStockPage(1); }}
                      className="filter-select"
                    >
                      <option value="">Todos</option>
                      {weights.map((w) => (
                        <option key={w.id_weight ?? w.id} value={w.id_weight ?? w.id}>{w.weight_name || w.name || '-'}</option>
                      ))}
                    </Form.Select>
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                {stockLoading && <div className="inventory-loading"><Spinner animation="border" /></div>}
                <div className="inventory-table-wrap">
                  <Table responsive striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Descripción</th>
                        <th>Producto</th>
                        <th>Color</th>
                        <th>Talla</th>
                        <th>Peso</th>
                        <th>Stock</th>
                        <th>Costo prom.</th>
                        <th>Costo último</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!stockLoading && filteredStockItems.length === 0 && (
                        <tr><td colSpan={9} className="text-center">Sin datos</td></tr>
                      )}
                      {filteredStockItems.map((it) => (
                        <tr key={it.id_item} onClick={() => handleRowClick(it.id_item)} className="clickable-row">
                          <td>{it.sku || '-'}</td>
                          <td>{it.description || '-'}</td>
                          <td>{it.product_name || '-'}</td>
                          <td>{getColorDisplay(it)}</td>
                          <td>{getSizeDisplay(it)}</td>
                          <td>{getWeightDisplay(it, weights)}</td>
                          <td>{formatNumber(it.stock)}</td>
                          <td>{formatterCOP.format(it.cost_avg ?? 0)}</td>
                          <td>{formatterCOP.format(it.cost_last ?? 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <div className="pagination-controls d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <Button size="sm" variant="outline-primary" disabled={stockPage <= 1} onClick={() => setStockPage((p) => p - 1)}>
                      Anterior
                    </Button>
                    <span className="mx-2">
                      Pág. {stockPage} / {stockTotalPages}
                      {(stockFilters.id_weight || stockFilters.id_color || stockFilters.id_size)
                        ? ` (${filteredStockItems.length} filtrados)` : ` (${stockTotalRows} registros)`}
                    </span>
                    <Button size="sm" variant="outline-primary" disabled={stockPage >= stockTotalPages} onClick={() => setStockPage((p) => p + 1)}>
                      Siguiente
                    </Button>
                  </div>
                  <Button size="sm" variant="outline-success" onClick={handleDownloadStockCSV} disabled={stockDownloading}>
                    {stockDownloading ? <><Spinner animation="border" size="sm" className="me-1" /> Descargando…</> : <><i className="fas fa-download me-1"></i> Descargar CSV</>}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="movements" title="Movimientos">
            <Card>
              <Card.Header className="movements-card-header">
                <div className="movements-filters-row">
                  <div className="filter-field-wrap">
                    <span className="filter-field-label">ID ítem</span>
                    <Form.Control
                      placeholder="Filtrar por ID"
                      value={movFilters.id_item}
                      onChange={(e) => { setMovFilters((f) => ({ ...f, id_item: e.target.value })); setMovPage(1); }}
                      className="filter-input"
                    />
                  </div>
                  <div className="filter-field-wrap">
                    <span className="filter-field-label">Desde</span>
                    <Form.Control
                      type="date"
                      value={movFilters.date_from}
                      onChange={(e) => { setMovFilters((f) => ({ ...f, date_from: e.target.value })); setMovPage(1); }}
                      className="filter-input"
                    />
                  </div>
                  <div className="filter-field-wrap">
                    <span className="filter-field-label">Hasta</span>
                    <Form.Control
                      type="date"
                      value={movFilters.date_to}
                      onChange={(e) => { setMovFilters((f) => ({ ...f, date_to: e.target.value })); setMovPage(1); }}
                      className="filter-input"
                    />
                  </div>
                </div>
                <div className="movements-segmented-row">
                <div className="segmented-control-wrap">
                  <span className="segmented-label">Movimiento</span>
                  <div className="segmented-control" role="group">
                    {[
                      { value: '', label: 'Todos' },
                      { value: 'in', label: 'Entradas' },
                      { value: 'out', label: 'Salidas' }
                    ].map((opt) => (
                      <button
                        key={opt.value || 'all'}
                        type="button"
                        className={`segmented-option ${movFilters.movement_type === opt.value ? 'active' : ''}`}
                        onClick={() => { setMovFilters((f) => ({ ...f, movement_type: opt.value })); setMovPage(1); }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="segmented-control-wrap">
                  <span className="segmented-label">Tipo</span>
                  <div className="segmented-control segmented-control-doc" role="group">
                    {[
                      { value: '', label: 'Todos' },
                      { value: 'APERTURA', label: 'Apertura' },
                      { value: 'VENTA', label: 'Venta' },
                      { value: 'AJUSTE', label: 'Ajuste' },
                      { value: 'COMPRA', label: 'Compra' }
                    ].map((opt) => (
                      <button
                        key={opt.value || 'all'}
                        type="button"
                        className={`segmented-option ${movFilters.doc_type === opt.value ? 'active' : ''}`}
                        onClick={() => { setMovFilters((f) => ({ ...f, doc_type: opt.value })); setMovPage(1); }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                </div>
              </Card.Header>
              <Card.Body>
                {movLoading && <div className="inventory-loading"><Spinner animation="border" /></div>}
                <div className="inventory-table-wrap">
                  <Table responsive striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>SKU</th>
                        <th>Tipo</th>
                        <th>Entrada</th>
                        <th>Salida</th>
                        <th>Costo unit.</th>
                        <th>Ref</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!movLoading && filteredMovements.length === 0 && (
                        <tr><td colSpan={7} className="text-center">Sin datos</td></tr>
                      )}
                      {filteredMovements.map((m) => (
                        <tr key={m.id_inventory_move ?? m.move_date + m.id_item}>
                          <td>{m.move_date || '-'}</td>
                          <td>{m.sku || '-'}</td>
                          <td>{m.doc_type || '-'}</td>
                          <td>{formatNumber(m.quantity_in)}</td>
                          <td>{formatNumber(m.quantity_out)}</td>
                          <td>{formatterCOP.format(m.unit_cost ?? 0)}</td>
                          <td>{m.ref_table && m.ref_id ? `${m.ref_table}#${m.ref_id}` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <div className="pagination-controls">
                  <Button size="sm" variant="outline-primary" disabled={movPage <= 1} onClick={() => setMovPage((p) => p - 1)}>Anterior</Button>
                  <span className="mx-2">Pág. {movPage} / {movTotalPages} ({movTotalRows} registros)</span>
                  <Button size="sm" variant="outline-primary" disabled={movPage >= movTotalPages} onClick={() => setMovPage((p) => p + 1)}>Siguiente</Button>
                </div>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="reports" title="Reportes">
            <Card>
              <Card.Header className="reports-card-header">
                <div className="reports-filters-row">
                  <div className="filter-field-wrap">
                    <span className="filter-field-label">Fecha desde</span>
                    <Form.Control
                      type="date"
                      value={reportDateFrom}
                      onChange={(e) => setReportDateFrom(e.target.value)}
                      className="filter-input"
                    />
                  </div>
                  <div className="filter-field-wrap">
                    <span className="filter-field-label">Fecha hasta</span>
                    <Form.Control
                      type="date"
                      value={reportDateTo}
                      onChange={(e) => setReportDateTo(e.target.value)}
                      className="filter-input"
                    />
                  </div>
                </div>
                <div className="reports-segmented-row">
                  <span className="segmented-label">Tipo de reporte</span>
                  <div className="segmented-control segmented-control-reports" role="group">
                    {[
                      { value: 'stock-summary', label: 'Stock por categoría' },
                      { value: 'low-stock', label: 'Stock bajo' },
                      { value: 'valuation', label: 'Valoración' },
                      { value: 'in-out-summary', label: 'Entradas/Salidas' }
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`segmented-option ${selectedReportType === opt.value ? 'active' : ''}`}
                        onClick={() => openReport(opt.value, opt.value === 'in-out-summary' ? { date_from: reportDateFrom, date_to: reportDateTo } : opt.value === 'low-stock' ? { threshold: 5 } : {})}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="reports-card-body">
                <p className="reports-hint">Selecciona un tipo de reporte para verlo.</p>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>

        <ItemDetailModal show={showItemDetail} onHide={() => setShowItemDetail(false)} idItem={selectedItemId} onRefresh={loadStock} weights={weights} />
        <AdjustModal show={showAdjust} onHide={() => setShowAdjust(false)} onSuccess={handleAdjustSuccess} items={allItemsForAdjust} />
        <ReportModal show={showReport} onHide={() => setShowReport(false)} reportType={reportType} params={reportParams} />
      </Container>
    </>
  );
};

export default InventoryManagement;
